// deps
import { useCallback, useContext } from "react";

// internal-deps
import {
    BLAZE_EVENTS,
    CheckoutActionTypes,
    CheckoutContext,
    CheckoutRoute,
    useNavigate,
} from "@swiggy-private/checkout-core";
import Blaze from "@swiggy-private/blaze.js";

// hooks
import { usePaymentLink } from "./usePaymentLink";

// constants
import { ERROR_STATUS_CODES, OOPS_SOMETHING_WENT_WRONG } from "@dineout-cart-client/constants";

// interfaces
import { IErrorMeta } from "@dineout-cart-client/interfaces";

interface IProps {
    setSoftError?: (softError: string) => void;
    setErrorPayload?: (errorPayload: IErrorMeta) => void;
    skipPaymentLink?: boolean;
}

interface IReturnType {
    redirect: (onError?: VoidFunction) => {};
}

const useRedirectToPayment = (props: IProps): IReturnType => {
    const { setSoftError, setErrorPayload, skipPaymentLink = false } = props;
    const { setCheckoutState } = useContext(CheckoutContext);
    const navigateTo = useNavigate();
    const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";
    const { generatePaymentLink } = usePaymentLink();

    const redirect = useCallback(
        async (onError?: VoidFunction) => {
            Blaze.recordStart(BLAZE_EVENTS.RECORD_DINEOUT_CHECKOUT_PAYMENT_LOAD_TIME);
            try {
                if (!skipPaymentLink) {
                    // Reuse the payment link generation logic from usePaymentLink hook
                    await generatePaymentLink();
                }

                // clear cached payment data before going to payment screen from cart
                if (isSessionStorageAvailable) {
                    sessionStorage.removeItem("show_payments");
                    sessionStorage.removeItem("with_coupon");
                    sessionStorage.setItem("isFromDineoutCart", "true");
                }

                setCheckoutState({
                    type: CheckoutActionTypes.SHOW_PAYMENTS,
                });
                navigateTo(CheckoutRoute.PAYMENTS, {}, false);
            } catch (e) {
                Blaze.recordEnd(BLAZE_EVENTS.RECORD_DINEOUT_CHECKOUT_PAYMENT_LOAD_TIME);
                const error = e as { error: IErrorMeta };
                if (error.error?.statusCode === ERROR_STATUS_CODES.QR_INVALID) {
                    // TODOs: need to replace this with setSoft error or added in context
                    setErrorPayload?.(error.error);
                } else {
                    setSoftError?.(OOPS_SOMETHING_WENT_WRONG);
                }
                onError?.();
            }
        },
        [
            isSessionStorageAvailable,
            setCheckoutState,
            navigateTo,
            setErrorPayload,
            setSoftError,
            generatePaymentLink,
            skipPaymentLink,
        ],
    );

    return {
        redirect,
    };
};

export default useRedirectToPayment;
